/**
 * Product Dev hooks.
 *
 * Thin wrappers over the productDev tRPC procedures. Access is gated by the
 * `product_dev.view` / `product_dev.manage` RBAC permissions (sidebar entry
 * + handler-level `requirePermission`). No env-var feature flag — same
 * pattern as Purchase Orders / Inventory.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getQueryKey } from '@trpc/react-query';
import { trpc, type RouterInputs, type RouterOutputs } from '@/lib/trpc';
import { useOrganization } from '@/contexts/OrganizationContext';
import { createSerialQueue } from '../utils/serial-queue';

type ProductDevDetail = RouterOutputs['productDev']['get'];
type ProductDevListRow = RouterOutputs['productDev']['list'][number];
type ProductDevVariantsList = RouterOutputs['productDev']['listVariants'];
type SetVariantsPayload = RouterInputs['productDev']['setVariants']['variants'];
type SetSampleQcChecks = RouterInputs['productDev']['setSampleQc']['checks'];

/**
 * Merge a wire update payload into a cached DTO, patching ONLY keys that already
 * exist on the cached object. Derived/joined fields (e.g. `supplier_title`,
 * `assigned_to_name`, `hero_image_url`, the landed-cost FX estimate) are never
 * in the update payload, so they pass through untouched and stay at their last
 * server value until the `onSettled` refetch reconciles them. This keeps the
 * optimistic paint honest — we never fabricate a derived value we can't compute
 * on the client.
 */
function mergeDtoPatch<T>(dto: T, updates: Record<string, unknown>): T {
  const base = dto as Record<string, unknown>;
  const patch: Record<string, unknown> = {};
  for (const key of Object.keys(updates)) {
    if (key in base) patch[key] = updates[key];
  }
  return { ...base, ...patch } as T;
}

interface ListFilters {
  includeArchived?: boolean;
  stage?: string | null;
  status?: string | null;
  supplierId?: string | null;
  productType?: 'blank' | 'finished_garment' | 'accessory' | null;
  recordType?: 'new_product' | 'product_change' | 'task' | null;
}

export function useProductDevList(filters: ListFilters = {}) {
  const { organization } = useOrganization();
  return trpc.productDev.list.useQuery(filters, {
    enabled: Boolean(organization?.id),
    // 5 min: the list now resolves signed hero-image URLs (10 min TTL) per row,
    // so refetch sparingly to avoid hammering the backend `createSignedUrl` call
    // (mirrors useProductDev's 5-min choice).
    staleTime: 5 * 60_000,
  });
}

/**
 * Distinct brand + capsule values for the caller's org, used to populate the
 * list-page grouping filters. These change rarely (they're user-entered
 * grouping labels), so a 5-min staleTime keeps the dropdowns cheap.
 */
export function useProductDevGroupingValues() {
  const { organization } = useOrganization();
  return trpc.productDev.listGroupingValues.useQuery(undefined, {
    enabled: Boolean(organization?.id),
    staleTime: 5 * 60_000,
  });
}

export function useProductDev(productDevId: string | undefined) {
  const { organization } = useOrganization();
  // 5 min staleTime keeps the page snappy without thrashing the Storage
  // `createSignedUrl` call on the backend (which fires per `get`). The signed
  // URL has a 10 min TTL so this comfortably stays inside that window.
  return trpc.productDev.get.useQuery(
    { productDevId: productDevId ?? '' },
    {
      enabled: Boolean(organization?.id && productDevId),
      staleTime: 5 * 60_000,
      refetchOnWindowFocus: false,
    },
  );
}

export function useProductDevVariants(productDevId: string | undefined) {
  return trpc.productDev.listVariants.useQuery(
    { productDevId: productDevId ?? '' },
    { enabled: Boolean(productDevId), staleTime: 30_000 },
  );
}

export function useProductDevAttributes(productDevId: string | undefined) {
  return trpc.productDev.listAttributes.useQuery(
    { productDevId: productDevId ?? '' },
    { enabled: Boolean(productDevId), staleTime: 30_000 },
  );
}

export function useProductDevFiles(productDevId: string | undefined) {
  return trpc.productDev.listFiles.useQuery(
    { productDevId: productDevId ?? '' },
    { enabled: Boolean(productDevId), staleTime: 30_000 },
  );
}

export function useProductDevTrims(productDevId: string | undefined) {
  return trpc.productDev.listTrims.useQuery(
    { productDevId: productDevId ?? '' },
    { enabled: Boolean(productDevId), staleTime: 30_000 },
  );
}

/**
 * SKU picker search for the Trims card. Only fires once the operator has typed
 * enough to be meaningful (>= 2 chars — the backend schema also rejects empty
 * queries), and keeps the previous page of results visible while the next
 * keystroke's query resolves so the list doesn't flash empty.
 */
export function useSearchSkus(query: string) {
  const trimmed = query.trim();
  return trpc.productDev.searchSkus.useQuery(
    { query: trimmed },
    {
      enabled: trimmed.length >= 2,
      staleTime: 60_000,
      placeholderData: (prev) => prev,
    },
  );
}

export function useProductDevSamples(productDevId: string | undefined) {
  return trpc.productDev.listSamples.useQuery(
    { productDevId: productDevId ?? '' },
    { enabled: Boolean(productDevId), staleTime: 15_000 },
  );
}

export function useSampleRoundPhotos(sampleRoundId: string | undefined) {
  return trpc.productDev.listSamplePhotos.useQuery(
    { sampleId: sampleRoundId ?? '' },
    { enabled: Boolean(sampleRoundId), staleTime: 15_000 },
  );
}

/**
 * @param entityId Optional. On a product_dev record this widens the picker to
 *   include the partner org the record is shared with, so the factory working
 *   on the style can be @mentioned. Omit it and you get own-org members only.
 */
export function useMentionableUsers(
  entityType: 'purchase_order' | 'product_dev' | 'shipment',
  enabled = true,
  entityId?: string,
) {
  return trpc.comments.listMentionableUsers.useQuery(
    { entityType, entityId },
    { staleTime: 5 * 60_000, enabled },
  );
}

/** Own-org users plus supplier users reachable through this exact Product Dev
 * selection. The server repeats the scope check when the comment is written. */
export function useProductDevBatchMentionableUsers(
  entityIds: string[],
  enabled = true,
) {
  return trpc.comments.listProductDevBatchMentionableUsers.useQuery(
    { entityIds },
    { staleTime: 5 * 60_000, enabled: enabled && entityIds.length > 0 },
  );
}

export function useLinkedDevForPurchaseOrder(purchaseOrderId: string | undefined) {
  return trpc.productDev.getLinkedDevSummaryForPurchaseOrder.useQuery(
    { purchaseOrderId: purchaseOrderId ?? '' },
    {
      enabled: Boolean(purchaseOrderId),
      staleTime: 60_000,
    },
  );
}

export function useProductDevMutations(productDevId?: string) {
  const utils = trpc.useUtils();
  const queryClient = useQueryClient();

  // Trailing debounce for `update`'s settle-invalidation (below): N rapid
  // queued field edits (see ProductDevDetail's updateQueueRef) each settle in
  // turn, but only the LAST one's invalidate actually needs to run — the
  // earlier ones are superseded before their refetch would even matter. Each
  // new settle clears + reschedules the timer, so exactly one invalidate
  // fires, 400ms after the LAST settle in a burst.
  const updateSettleTimerRef = useRef<number | null>(null);

  const invalidateList = () => utils.productDev.list.invalidate();
  const invalidateOne = (id: string) =>
    void utils.productDev.get.invalidate({ productDevId: id });
  const invalidateVariants = (id: string) =>
    void utils.productDev.listVariants.invalidate({ productDevId: id });
  const invalidateAttributes = (id: string) =>
    void utils.productDev.listAttributes.invalidate({ productDevId: id });
  const invalidateFiles = (id: string) =>
    void utils.productDev.listFiles.invalidate({ productDevId: id });
  const invalidateTrims = (id: string) =>
    void utils.productDev.listTrims.invalidate({ productDevId: id });

  const create = trpc.productDev.create.useMutation({
    onSuccess: () => invalidateList(),
  });

  // Optimistic inline-field edits. The detail page's `updateField` (and every
  // InlineEdit / InlineSelect / PriorityFlag / status + stage control) routes
  // through this mutation, so patching the cache in `onMutate` makes the new
  // value paint instantly for the operator instead of waiting on the ~500ms
  // server round-trip + refetch. `onSettled` still invalidates so server truth
  // (derived fields, FX estimate, joined labels) reconciles.
  const update = trpc.productDev.update.useMutation({
    onMutate: async ({ productDevId: id, updates }) => {
      const patch = updates as Record<string, unknown>;
      const listKey = getQueryKey(trpc.productDev.list);

      // Stop in-flight fetches from racing the optimistic paint.
      await utils.productDev.get.cancel({ productDevId: id });
      await queryClient.cancelQueries({ queryKey: listKey });

      // Snapshot for rollback.
      const prevDetail = utils.productDev.get.getData({ productDevId: id });
      const prevLists = queryClient.getQueriesData<ProductDevListRow[]>({
        queryKey: listKey,
      });

      // Patch the detail query.
      utils.productDev.get.setData({ productDevId: id }, (old) =>
        old ? mergeDtoPatch<ProductDevDetail>(old, patch) : old,
      );

      // Patch the matching row in every cached list variant (the list row shows
      // stage / status / priority / brand / capsule etc.).
      queryClient.setQueriesData<ProductDevListRow[]>({ queryKey: listKey }, (old) =>
        old
          ? old.map((row) => (row.id === id ? mergeDtoPatch(row, patch) : row))
          : old,
      );

      return { prevDetail, prevLists, id };
    },
    onError: (_err, _vars, context) => {
      if (!context) return;
      // Roll both snapshots back; the caller's existing error toast still fires.
      utils.productDev.get.setData({ productDevId: context.id }, context.prevDetail);
      for (const [key, data] of context.prevLists) {
        queryClient.setQueryData(key, data);
      }
    },
    onSettled: (_data, _err, vars) => {
      if (updateSettleTimerRef.current != null) {
        window.clearTimeout(updateSettleTimerRef.current);
      }
      updateSettleTimerRef.current = window.setTimeout(() => {
        updateSettleTimerRef.current = null;
        invalidateList();
        invalidateOne(vars.productDevId);
      }, 400);
    },
  });

  const archive = trpc.productDev.archive.useMutation({
    onSuccess: () => invalidateList(),
  });

  // Bulk field-set across selected list rows (priority / status / expected
  // date / assignee). Invalidates the list so the flags, pills, and dates
  // re-render for every affected row.
  const batchUpdate = trpc.productDev.batchUpdate.useMutation({
    onSuccess: () => invalidateList(),
  });

  // One explicit note copied to every selected Product Dev timeline. The
  // backend groups mention delivery so this remains one deliberate action,
  // not a notification fan-out storm.
  const batchComment = trpc.comments.addProductDevBatchComment.useMutation({
    onSuccess: () => {
      void utils.comments.listTimeline.invalidate();
    },
  });

  const batchUploadFile = trpc.productDev.batchUploadFile.useMutation({
    onSuccess: () => {
      void utils.productDev.listFiles.invalidate();
    },
  });

  // No onSuccess invalidate: every write flows through setVariantsSerialized,
  // which writes the server-confirmed rows straight into the cache before
  // releasing its queue slot — a background refetch here is redundant churn
  // (review nit).
  const setVariants = trpc.productDev.setVariants.useMutation();

  // Shared serialization queue for `setVariants`. BuyPlanCard and
  // VariantsCard (colour/size/pantone edits, add/remove colourway, and the
  // status pill — all via VariantsCard's `persist`) each resend the WHOLE
  // variant array on every edit; without a SHARED queue, a save from one card
  // can be in flight while the other fires an independent save, and whichever
  // resolves last wins — built from whatever rows IT captured, silently
  // reverting the other card's change. `setVariantsSerialized` fixes this at
  // the hook level: every caller enqueues here in click order, and the caller
  // passes a payload BUILDER (not a pre-built array) that is only invoked once
  // the task reaches the front of the queue — so it always reads whatever's
  // freshest at that moment via `getCachedVariants`, never a stale snapshot
  // taken back when the save was requested.
  //
  // Each completed save's server-confirmed result is written straight into
  // the `listVariants` cache before the queue advances (rather than relying
  // on the mutation's onSuccess invalidate + background refetch above), so
  // the NEXT queued builder — and any consumer gated on `setVariantsQueueDepth`
  // dropping to 0 — sees fresh data the instant this save's slot completes.
  const setVariantsQueueRef = useRef(createSerialQueue());
  const [setVariantsQueueDepth, setSetVariantsQueueDepth] = useState(0);

  const getCachedVariants = useCallback(
    (id: string): ProductDevVariantsList | undefined =>
      utils.productDev.listVariants.getData({ productDevId: id }),
    [utils],
  );

  const setVariantsSerialized = useCallback(
    (id: string, buildVariants: () => SetVariantsPayload) => {
      setSetVariantsQueueDepth((n) => n + 1);
      const run = setVariantsQueueRef.current.enqueue(async () => {
        const data = await setVariants.mutateAsync({
          productDevId: id,
          variants: buildVariants(),
        });
        utils.productDev.listVariants.setData({ productDevId: id }, data);
        return data;
      });
      run.then(
        () => setSetVariantsQueueDepth((n) => n - 1),
        () => setSetVariantsQueueDepth((n) => n - 1),
      );
      return run;
    },
    [setVariants, utils],
  );

  /** Optimistically stamp a colourway's status into the variants cache.
   *  Both VariantsCard and BuyPlanCard rebuild their save payload from this
   *  cache at execution time (see `setVariantsSerialized`); without this, a
   *  buy-plan save queued right after a status click would rebuild before the
   *  status mutation's own cache write lands and resend the STALE status,
   *  silently reverting a just-clicked approval. Case-sensitive: `colour` is
   *  the server's upsert conflict key (`organization_id,product_dev_id,colour`,
   *  no lower() normalization), so two colourways differing only in case are
   *  distinct rows — a case-insensitive match here would stamp both. */
  const optimisticVariantStatus = (productDevId: string, colour: string, status: string) => {
    utils.productDev.listVariants.setData({ productDevId }, (old) =>
      old?.map((v) => (v.colour === colour ? { ...v, status: status as typeof v.status } : v)),
    );
  };

  const setAttributes = trpc.productDev.setAttributes.useMutation({
    onSuccess: (_data, vars) => invalidateAttributes(vars.productDevId),
  });

  // Full-replace reconcile of the trims list (mirrors setVariants / setAttributes).
  // The card resends the WHOLE trim array on every edit, so a single invalidate
  // on success reconciles the freshly-signed image URLs + joined sku_code.
  const setTrims = trpc.productDev.setTrims.useMutation({
    onSuccess: (_data, vars) => invalidateTrims(vars.productDevId),
  });

  const uploadFile = trpc.productDev.uploadFile.useMutation({
    onSuccess: (_data, vars) => invalidateFiles(vars.productDevId),
  });

  const deleteFile = trpc.productDev.deleteFile.useMutation({
    onSuccess: () => {
      if (productDevId) invalidateFiles(productDevId);
    },
  });

  const invalidateSamples = (id: string) =>
    void utils.productDev.listSamples.invalidate({ productDevId: id });

  /** Sample writes can move the PARENT record's status server-side: the first
   *  round flips `briefing → awaiting_sample` (`markAwaitingSampleIfBriefing`).
   *  Invalidating only the samples list left the detail header and the list row
   *  showing "Briefing" until a hard reload (BLU-629 M3). */
  const invalidateSamplesAndRecord = (id: string) => {
    invalidateSamples(id);
    invalidateOne(id);
    invalidateList();
  };

  const upsertSample = trpc.productDev.upsertSample.useMutation({
    onSuccess: (_data, vars) => invalidateSamplesAndRecord(vars.productDevId),
  });

  const deleteSample = trpc.productDev.deleteSample.useMutation({
    onSuccess: () => {
      if (productDevId) invalidateSamplesAndRecord(productDevId);
    },
  });

  /** Save one round's QC checklist. The checklist is orthogonal to the round's
   *  status, so this never touches the parent record — only the samples list.
   *  Always call it through `setSampleQcSerialized`, never directly. */
  const setSampleQc = trpc.productDev.setSampleQc.useMutation({
    onError: () => {
      // The optimistic stamp below is now wrong — pull the truth back.
      if (productDevId) invalidateSamples(productDevId);
    },
  });

  // Same lost-update shape as `setVariantsSerialized` above, for the same reason:
  // `setSampleQc` is a WHOLE-checklist replace fired from a click-cycling pill, so
  // two saves in flight together (unset → pass → fail is three clicks, and it is
  // the expected way to reach "n-a") can resolve out of order and repaint a stale
  // verdict over the newest one. Every save enqueues here in click order and its
  // server-confirmed row is written into the cache before the queue advances, so
  // the next builder — and the eventual paint — always sees the latest truth.
  const setSampleQcQueueRef = useRef(createSerialQueue());

  const setSampleQcSerialized = useCallback(
    (sampleRoundId: string, buildChecks: () => SetSampleQcChecks) =>
      setSampleQcQueueRef.current.enqueue(async () => {
        const data = await setSampleQc.mutateAsync({
          sampleRoundId,
          checks: buildChecks(),
        });
        if (productDevId) {
          // Reconcile from the server's row rather than a blanket invalidate, so
          // a verdict click doesn't re-fetch (and re-sign) every round's photos.
          utils.productDev.listSamples.setData({ productDevId }, (old) =>
            old?.map((row) => (row.id === data.id ? data : row)),
          );
        }
        return data;
      }),
    [setSampleQc, productDevId, utils],
  );

  /** Optimistically paint a QC verdict before the save lands. Mirrors the
   *  colourway-status stamp above: the checklist rebuilds its payload from this
   *  cache, so without it a second click queued behind the first would resend
   *  the stale checklist and silently revert the first verdict. */
  const optimisticSampleQc = (
    sampleRoundId: string,
    qcChecks: Record<string, { result: string; note: string | null } | undefined>,
  ) => {
    if (!productDevId) return;
    utils.productDev.listSamples.setData({ productDevId }, (old) =>
      old?.map((row) =>
        row.id === sampleRoundId
          ? { ...row, qc_checks: qcChecks as typeof row.qc_checks }
          : row,
      ),
    );
  };

  const uploadVariantPhoto = trpc.productDev.uploadVariantPhoto.useMutation({
    onSuccess: () => {
      if (productDevId) {
        invalidateVariants(productDevId);
        // The hero image auto-derives from variant photos — invalidate the
        // detail query too so the header re-resolves.
        invalidateOne(productDevId);
      }
    },
  });

  const uploadVariantSwatch = trpc.productDev.uploadVariantSwatch.useMutation({
    onSuccess: () => {
      if (productDevId) invalidateVariants(productDevId);
    },
  });

  const duplicate = trpc.productDev.duplicate.useMutation({
    onSuccess: () => invalidateList(),
  });

  // Graduate a dev record into a purchase order (new PO or append to an
  // existing one). The backend sets `linked_purchase_order_id` on the dev
  // record, so we invalidate the dev queries alongside the PO queries.
  const addToPurchaseOrder = trpc.productDev.addToPurchaseOrder.useMutation({
    onSuccess: (_data, vars) => {
      invalidateList();
      invalidateOne(vars.productDevId);
      void utils.purchaseOrders.getOrders.invalidate();
    },
  });

  const uploadSamplePhoto = trpc.productDev.uploadSamplePhoto.useMutation({
    onSuccess: (_data, vars) => {
      void utils.productDev.listSamplePhotos.invalidate({
        sampleId: vars.sampleRoundId,
      });
    },
  });

  const deleteSamplePhoto = trpc.productDev.deleteSamplePhoto.useMutation({
    onSuccess: () => {
      void utils.productDev.listSamplePhotos.invalidate();
    },
  });

  const updateSamplePhotoCaption = trpc.productDev.updateSamplePhotoCaption.useMutation({
    onSuccess: () => {
      void utils.productDev.listSamplePhotos.invalidate();
    },
  });

  useEffect(
    () => () => {
      if (updateSettleTimerRef.current != null) window.clearTimeout(updateSettleTimerRef.current);
    },
    [],
  );

  return {
    create,
    update,
    archive,
    batchUpdate,
    batchComment,
    batchUploadFile,
    setVariants,
    setVariantsSerialized,
    setVariantsQueueDepth,
    getCachedVariants,
    optimisticVariantStatus,
    setAttributes,
    setTrims,
    uploadFile,
    deleteFile,
    upsertSample,
    deleteSample,
    setSampleQcSerialized,
    optimisticSampleQc,
    uploadVariantPhoto,
    uploadVariantSwatch,
    duplicate,
    addToPurchaseOrder,
    uploadSamplePhoto,
    deleteSamplePhoto,
    updateSamplePhotoCaption,
  };
}
